import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Graph from "../../common/Graph";
import { getDoctorRecord } from "../../../redux/Thunk/Doctor/DoctorThunks";
import Loader from "../../Loader";

const Records = () => {
  const dispatch = useDispatch();
  const { doctorRecord, loading, doctorDetails } = useSelector(
    (state) => state.doctor
  );
  const [records, setRecords] = useState([]);

  useEffect(() => {
    const id = doctorDetails?.doctor?.DID;
    if (id) {
      dispatch(getDoctorRecord(id));
    }
  }, [dispatch, doctorDetails?.doctor?.DID]);

  useEffect(() => {
    if (doctorRecord?.length > 0) {
      const publishedRecords = doctorRecord?.filter((record) => record?.isPublish);
      // Ensure we display most recent records first by sorting
      const sortedRecords = publishedRecords?.sort((a, b) => new Date(b.date) - new Date(a.date));
      setRecords(sortedRecords);
    } else {
      setRecords([]);
    }
  }, [doctorRecord]);

  return (
    <div className="w-full h-full mx-auto p-4 md:p-8 bg-transparent">
      <Loader loading={loading} />

      {/* Header section */}
      <div className="mb-8 flex flex-col md:flex-row md:items-end justify-between border-b border-gray-100 pb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">Financial Records</h1>
          <p className="text-gray-500 mt-2 text-sm md:text-base max-w-3xl">
            Track your cumulative generated fees, payment histories, and order volume (gateways) across billing periods.
            All published records from administration appear here.
          </p>
        </div>
        <div className="mt-4 md:mt-0">
          <span className="bg-primary-50 border border-primary-100 text-primary-600 font-semibold py-1.5 px-4 rounded-full text-sm shadow-sm inline-flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            {records?.length || 0} Records
          </span>
        </div>
      </div>

      <div className="mt-6">
        {records?.length > 0 ? (
          <div className="space-y-6">
            {records?.map((record, i) => {
              const formattedDate = new Date(record?.date).toLocaleDateString("en-GB");

              return (
                <div key={i} className="bg-white rounded-[24px] p-6 lg:p-8 flex flex-col xl:flex-row gap-8 items-stretch shadow-sm border border-gray-100/80 hover:shadow-lg transition-all duration-300 relative overflow-hidden group">

                  {/* Left Accent Bar */}
                  <div className="absolute top-0 left-0 w-1.5 h-full bg-gradient-to-b from-primary-400 to-primary-600 opacity-80"></div>

                  {/* Data Details Block */}
                  <div className="flex-1 flex flex-col justify-between pl-2">

                    {/* Top Context & Period */}
                    <div className="flex flex-wrap justify-between items-start mb-8 gap-4">
                      <div>
                        <p className="text-xs font-bold text-gray-400 tracking-widest uppercase mb-1.5 flex items-center gap-1.5">
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          Billing Period
                        </p>
                        <h2 className="text-2xl lg:text-3xl font-black text-gray-900 tracking-tight">{record?.period || "N/A"}</h2>
                      </div>
                      <div className="flex flex-col items-end gap-2 text-right">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-50 border border-gray-200 text-gray-600 text-[10px] font-bold uppercase tracking-wider rounded-md">
                          <span className="w-1.5 h-1.5 rounded-full bg-success-500 animate-pulse"></span>
                          Published
                        </span>
                        <span className="text-[11px] font-mono text-gray-400 bg-gray-50 px-2 rounded border border-gray-100">
                          FID: {record?.FID || "N/A"}
                        </span>
                      </div>
                    </div>

                    {/* Main Metric Strip */}
                    <div className="grid grid-cols-2 gap-4 lg:gap-8 mb-8">
                      <div className="bg-gradient-to-br from-green-50 to-green-50/20 p-5 rounded-2xl border border-green-100 relative overflow-hidden">
                        <div className="absolute -right-4 -bottom-4 opacity-[0.03] text-green-900">
                          <svg className="w-32 h-32" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                            <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <p className="text-green-700 text-[11px] font-bold uppercase tracking-widest mb-1.5 relative z-10">Total Fee Generated</p>
                        <p className="text-3xl lg:text-4xl font-black text-green-800 relative z-10 truncate" title={record?.paidRent ? `₹${parseFloat(record.paidRent).toLocaleString('en-IN')}` : "₹0"}>
                          {record?.paidRent ? `₹${parseFloat(record.paidRent).toLocaleString('en-IN')}` : "₹0"}
                        </p>
                      </div>

                      <div className="bg-gradient-to-br from-primary-50 to-primary-50/20 p-5 rounded-2xl border border-primary-100 relative overflow-hidden">
                        <div className="absolute -right-4 -bottom-4 opacity-[0.03] text-primary-900">
                          <svg className="w-32 h-32" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                          </svg>
                        </div>
                        <p className="text-primary-700 text-[11px] font-bold uppercase tracking-widest mb-1.5 relative z-10">Orders (Gateways)</p>
                        <p className="text-3xl lg:text-4xl font-black text-primary-800 relative z-10 truncate" title={record?.gateways}>
                          {record?.gateways || "0"}
                        </p>
                      </div>
                    </div>

                    {/* Payment Meta Details */}
                    <div className="bg-gray-50/80 rounded-2xl p-5 border border-gray-100 mt-auto">
                      <div className="flex justify-between items-end mb-4 border-b border-gray-200/60 pb-3">
                        <p className="text-gray-900 font-bold text-sm">Payment Details</p>
                        <p className="text-xs font-medium text-gray-500 flex items-center gap-1.5">
                          <svg className="w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Sent: {formattedDate}
                        </p>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-y-4 gap-x-6">
                        <div>
                          <p className="text-gray-400 text-[10px] font-bold uppercase tracking-widest mb-1">Transaction Type</p>
                          <p className="text-gray-800 font-semibold text-sm">
                            {record?.trxType ? record.trxType.toUpperCase() : "N/A"}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-400 text-[10px] font-bold uppercase tracking-widest mb-1">Account</p>
                          <p className="text-gray-800 font-semibold text-sm truncate" title={record?.account}>{record?.account || "N/A"}</p>
                        </div>
                        <div>
                          <p className="text-gray-400 text-[10px] font-bold uppercase tracking-widest mb-1">Transaction Ref</p>
                          <p className="text-gray-800 font-semibold text-sm truncate" title={record?.trxId}>
                            <span className="font-mono text-primary-600">{record?.trxId || "N/A"}</span>
                          </p>
                        </div>
                      </div>
                    </div>

                  </div>

                  {/* Graph Details Block */}
                  <div className="w-full xl:w-[460px] shrink-0 pt-6 xl:pt-0 flex flex-col border-t xl:border-t-0 xl:border-l border-gray-100 xl:pl-8 justify-center">
                    <div className="bg-white rounded-2xl h-full flex flex-col justify-center">
                      <div className="flex items-center justify-between mb-6">
                        <h3 className="text-sm font-bold text-gray-700">Gateway Activity Trend</h3>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-primary-500 bg-primary-50 px-2 py-1 rounded">HealthMap</span>
                      </div>
                      <div className="w-full relative h-[220px]">
                        {record?.detail && record.detail.length > 0 ? (
                          <Graph graphData={record?.detail} />
                        ) : (
                          <div className="absolute inset-0 flex items-center justify-center bg-gray-50/50 rounded-xl border border-dashed border-gray-200">
                            <p className="text-xs text-gray-400 font-medium tracking-wide">Not enough data to graph</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-20 bg-white border border-dashed border-gray-200 rounded-3xl w-full mt-4 flex flex-col items-center">
            <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-5 shadow-inner">
              <svg className="w-8 h-8 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-800">No Records Found</h3>
            <p className="text-gray-500 mt-2 max-w-sm mx-auto text-sm leading-relaxed">
              Once administrative processing is complete, your payment records and fee details will appear here. No published records exist yet.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Records;
